import { Box } from "@mui/material";
import { UseQueryResult } from "react-query";

import { pickKeys } from "../../../helpers/array";
import {
  pieSeriesHasValues,
  prependZeroDataPoint,
  toPieChartData,
} from "../../../helpers/charts";
import {
  toDollars,
  toFormattedNumber,
  toThousands,
} from "../../../helpers/currencies";
import { formatDate } from "../../../helpers/dates";
import ReactELineChart from "../ECharts/ReactELineChart";
import ReactEPieChart from "../ECharts/ReactEPieChart";

export type APChartType = "line" | "pie";

export type APChartAxisOptions = {
  type: "time" | "currency" | "count";
};

export type APChartDimension<T> = {
  key: keyof T;
  label: string;
  sliceLabels?: Record<string, string>;
};

export type APChartDimensions<T> = APChartDimension<T>[];

type AxisFormatters = {
  formatter?: (value: string | number) => string;
  tooltipFormatter?: (value: string | number) => string;
};

const axisFormatters: Record<APChartAxisOptions["type"], AxisFormatters> = {
  time: {
    formatter: (value: string | number): string => {
      return formatDate(value as string, "YYYY-MM");
    },
  },
  currency: {
    formatter: (value: string | number): string => {
      return toThousands(value as number, true);
    },
    tooltipFormatter: (value: string | number): string => {
      return toDollars(value as number, 0);
    },
  },
  count: {
    formatter: (value: string | number): string => {
      return toThousands(value as number);
    },
    tooltipFormatter: (value: string | number): string => {
      return toFormattedNumber(value as number, 0);
    },
  },
};

const axisTypeToEChartType: Record<
  APChartAxisOptions["type"],
  "category" | "value" | "time"
> = {
  time: "time",
  currency: "value",
  count: "value",
};

type APChartSharedProps<TData> = {
  chartTitle?: string;
  totalsRenderer?: (data: TData | undefined) => React.ReactNode;
  customColors?: string[];
  customHeight?: number;
};

export type APLineChartProps<TDatum extends Record<string, number | string>> =
  APChartSharedProps<TDatum[]> & {
    type: "line";
    getData: () => UseQueryResult<TDatum[], unknown>;
    xAxisOptions: APChartAxisOptions;
    yAxisOptions: APChartAxisOptions;
    dimensions: APChartDimensions<TDatum>;
    isAreaChart?: boolean;
    isStacked?: boolean;
  };

export type APPieChartProps<TData extends Record<string, unknown>> =
  APChartSharedProps<TData> & {
    type: "pie";
    getData: () => UseQueryResult<TData, unknown>;
    dimensions: APChartDimensions<TData>;
    valueOptions?: APChartAxisOptions;
  };

const LINE_CHART_HEIGHT = 500;
const PIE_CHART_HEIGHT = 360;
const MOBILE_PIE_CHART_HEIGHT = 280;

const renderLineChart = <TDatum extends Record<string, number | string>>({
  data,
  isSuccess,
  dimensions,
  xAxisOptions,
  yAxisOptions,
  chartTitle,
  isAreaChart,
  isStacked,
  customColors,
}: {
  data: TDatum[];
  isSuccess: boolean;
  dimensions: APChartDimensions<TDatum>;
  xAxisOptions: APChartAxisOptions;
  yAxisOptions: APChartAxisOptions;
  chartTitle?: string;
  isAreaChart?: boolean;
  isStacked?: boolean;
  customColors?: string[];
}) => {
  const _data = pickKeys(
    data,
    dimensions.map((dimension) => dimension.key)
  );

  return (
    <ReactELineChart<TDatum>
      title={chartTitle}
      data={isSuccess ? prependZeroDataPoint(_data, "date") : []}
      dimensions={dimensions.map((dimension) => dimension.key)}
      dimensionLabels={dimensions.reduce((acc, dimension) => {
        acc[dimension.key] = dimension.label;
        return acc;
      }, {} as Record<keyof TDatum, string>)}
      xAxisOptions={{
        type: axisTypeToEChartType[xAxisOptions.type],
        formatter: axisFormatters[xAxisOptions.type].formatter,
      }}
      yAxisOptions={{
        formatter: axisFormatters[yAxisOptions.type].formatter,
        tooltipFormatter: axisFormatters[yAxisOptions.type].tooltipFormatter,
      }}
      areaChart={isAreaChart}
      stackChart={isStacked}
      customColors={customColors}
    />
  );
};

const renderPieCharts = <TData extends Record<string, unknown>>({
  data,
  dimensions,
  valueOptions,
  customColors,
}: {
  data: TData;
  dimensions: APChartDimensions<TData>;
  valueOptions: APChartAxisOptions;
  customColors?: string[];
}) => {
  const formatters = axisFormatters[valueOptions.type];
  const chartHeight =
    dimensions.length === 1 ? LINE_CHART_HEIGHT : PIE_CHART_HEIGHT;
  const mobileChartHeight = MOBILE_PIE_CHART_HEIGHT;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: dimensions.length > 1 ? "1fr 1fr" : "1fr",
        },
        columnGap: 2,
        rowGap: 4,
      }}
    >
      {dimensions.map((dimension) => {
        const seriesDatum = data[dimension.key];

        if (!seriesDatum || typeof seriesDatum !== "object") {
          return null;
        }

        const pieData = toPieChartData(seriesDatum, dimension.sliceLabels);

        return (
          <Box
            key={String(dimension.key)}
            sx={{ height: { xs: mobileChartHeight, sm: chartHeight } }}
          >
            <ReactEPieChart<{ name: string; value: number }>
              title={dimension.label}
              data={pieData}
              dimensions={["name", "value"]}
              dimensionLabels={{
                name: "Name",
                value: dimension.label,
              }}
              valueOptions={{
                formatter: formatters.formatter,
                tooltipFormatter: formatters.tooltipFormatter,
              }}
              customColors={customColors}
            />
          </Box>
        );
      })}
    </Box>
  );
};

function APChart<TDatum extends Record<string, number | string>>(
  props: APLineChartProps<TDatum>
): JSX.Element;
function APChart<TData extends Record<string, unknown>>(
  props: APPieChartProps<TData>
): JSX.Element;
function APChart<
  TDatum extends Record<string, number | string>,
  TData extends Record<string, unknown>
>(props: APLineChartProps<TDatum> | APPieChartProps<TData>): JSX.Element {
  const { data, isSuccess } = props.getData();

  if (props.type === "pie") {
    const pieData = data as TData | undefined;
    const visibleDimensions = props.dimensions.filter((dimension) =>
      pieSeriesHasValues(pieData?.[dimension.key])
    );
    const hasPieData = isSuccess && visibleDimensions.length > 0;

    return (
      <>
        <Box
          sx={{
            minHeight:
              !isSuccess || hasPieData
                ? visibleDimensions.length > 1
                  ? PIE_CHART_HEIGHT
                  : LINE_CHART_HEIGHT
                : 0,
          }}
        >
          {hasPieData &&
            pieData &&
            renderPieCharts({
              data: pieData,
              dimensions: visibleDimensions,
              valueOptions: props.valueOptions ?? { type: "count" },
              customColors: props.customColors,
            })}
        </Box>
        <Box sx={{ mt: 2 }}>
          {props.totalsRenderer && props.totalsRenderer(pieData)}
        </Box>
      </>
    );
  }

  const lineData = data as TDatum[] | undefined;
  const hasLineData = isSuccess && !!lineData && lineData.length > 0;

  let height = 0;

  if (!isSuccess || hasLineData) {
    height = props.customHeight ?? LINE_CHART_HEIGHT;
  }

  return (
    <>
      <Box sx={{ height }}>
        {hasLineData &&
          renderLineChart({
            data: lineData,
            isSuccess,
            dimensions: props.dimensions,
            xAxisOptions: props.xAxisOptions,
            yAxisOptions: props.yAxisOptions,
            chartTitle: props.chartTitle,
            isAreaChart: props.isAreaChart,
            isStacked: props.isStacked,
            customColors: props.customColors,
          })}
      </Box>

      {props.totalsRenderer && (
        <Box sx={{ mt: 2 }}>{props.totalsRenderer(lineData)}</Box>
      )}
    </>
  );
}

export default APChart;
